import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import ClientChatBubble from "./ClientChatBubble";
import {
  LayoutDashboard,
  Users2,
  Settings,
  LogOut,
  Scissors,
  ShieldCheck,
  Store,
  MessageSquare,
  Hammer,
  Rocket,
  BookOpen,
  Menu,
  Sparkles,
  Lock,
  FileSignature,
} from "lucide-react";
import { DpaBanner } from "./DpaBanner";

function roleBadge(role, status) {
  if (role === "admin") return "Amministratore";
  if (role === "agency") return "Agenzia";
  if (role === "client") return "Cliente";
  return status || role;
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const displayName =
    user?.role === "client"
      ? user?.name_salone
      : user?.full_name || user?.email;

  const initials = (
    user?.role === "client"
      ? `${user?.referente_nome?.[0] || ""}${user?.referente_cognome?.[0] || ""}`
      : (user?.full_name || user?.email || "MP")
          .split(" ")
          .map((s) => s[0])
          .slice(0, 2)
          .join("")
  ).toUpperCase();

  const pollUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/chat/unread-count");
      setUnread(data.unread || 0);
    } catch (e) {
      // silent
    }
  }, [user]);

  useEffect(() => {
    pollUnread();
    const t = setInterval(pollUnread, 15000);
    return () => clearInterval(t);
  }, [pollUnread]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Scroll to top on every route change (fixes ClientDetail opening at bottom)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  const isStaff =
    user?.role === "admin" || (user?.role === "agency" && user?.status === "approved");
  const isClient = user?.role === "client";

  const baseNav = [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/dashboard/impostazioni", label: "Impostazioni", icon: Settings },
    ...(user?.role === "client" && user?.plan === "pro" ? [{ to: "/dashboard/accordo", label: "Accordo art. 28", icon: FileSignature }] : []),
  ];
  const staffNav = isStaff
    ? [
        { to: "/dashboard/produzione", label: "Produzione", icon: Hammer },
        { to: "/dashboard/coda-metricool", label: "Coda Video", icon: Rocket },
        { to: "/dashboard/coda-metricool-foto", label: "Coda Foto", icon: Rocket },
        { to: "/dashboard/rubrica", label: "Rubrica Video", icon: BookOpen },
        { to: "/dashboard/rubrica-foto", label: "Rubrica Foto", icon: BookOpen },
        { to: "/dashboard/clienti", label: "Clienti", icon: Store },
        {
          to: "/dashboard/messaggi",
          label: "Messaggi",
          icon: MessageSquare,
          badge: unread,
        },
      ]
    : [];
  const clientNav = isClient
    ? [
        {
          to: "/dashboard/chat",
          label: "Chat agenzia",
          icon: MessageSquare,
          badge: unread,
        },
      ]
    : [];
  const adminNav =
    user?.role === "admin"
      ? [
          {
            to: "/dashboard/approvazioni",
            label: "Approvazioni agenzia",
            icon: ShieldCheck,
          },
          { to: "/dashboard/utenti", label: "Utenti", icon: Users2 },
          {
            to: "/dashboard/diagnostica-idee",
            label: "Diagnostica idee",
            icon: Sparkles,
          },
          { to: "/dashboard/sicurezza", label: "Sicurezza", icon: Lock },
          { to: "/dashboard/accordi", label: "Accordi art. 28", icon: FileSignature },
        ]
      : [];

  const links = [
    ...baseNav.slice(0, 1),
    ...clientNav,
    ...staffNav,
    ...adminNav,
    ...baseNav.slice(1),
  ];

  const NavLinks = ({ onNavigate }) => (
    <nav className="flex-1 px-3 py-4 space-y-1">
      {links.map(({ to, label, icon: Icon, end, badge }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`sidebar-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 h-10 rounded-lg text-sm transition-colors ${
              isActive
                ? "bg-[var(--mp-ink)] text-white"
                : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)] hover:bg-[var(--mp-dash-bg)]"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          <span className="flex-1">{label}</span>
          {badge > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--mp-primary)] text-white text-[10px] font-bold">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  const BrandHeader = () => (
    <Link to="/" className="flex items-center gap-2.5" data-testid="dashboard-brand">
      <div className="h-9 w-9 rounded-xl bg-[var(--mp-ink)] grid place-items-center">
        <Scissors className="h-4 w-4 text-white" strokeWidth={2.5} />
      </div>
      <div className="leading-tight">
        <div className="font-display text-[14px] font-semibold tracking-tight">
          Marketing Parrucchieri
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
          Dashboard
        </div>
      </div>
    </Link>
  );

  const UserFooter = () => (
    <div className="p-3 border-t border-[var(--mp-border)]">
      <div className="flex items-center gap-3 px-2 py-2">
        <div className="h-9 w-9 rounded-full bg-[var(--mp-yellow)] grid place-items-center font-display text-sm">
          {initials || "MP"}
        </div>
        <div className="leading-tight overflow-hidden">
          <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
            {displayName}
          </div>
          <div className="text-[10px] uppercase font-bold tracking-[0.18em] text-[var(--mp-muted)]">
            {roleBadge(user?.role, user?.status)}
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        data-testid="sidebar-logout"
        onClick={async () => {
          await logout();
          navigate("/");
        }}
        className="w-full justify-start text-sm mt-1"
      >
        <LogOut className="h-4 w-4 mr-2" /> Esci
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--mp-dash-bg)] text-[var(--mp-ink)] font-body grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col border-r border-[var(--mp-border)] bg-white">
        <div className="h-16 px-5 border-b border-[var(--mp-border)] flex items-center">
          <BrandHeader />
        </div>
        <NavLinks />
        <UserFooter />
      </aside>

      <main className="min-h-screen">
        {/* Mobile header with hamburger */}
        <header className="lg:hidden h-14 px-4 bg-white border-b border-[var(--mp-border)] flex items-center justify-between sticky top-0 z-30">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                data-testid="mobile-menu-trigger"
                className="h-10 w-10 p-0"
                aria-label="Apri menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 flex flex-col bg-white">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="h-16 px-5 border-b border-[var(--mp-border)] flex items-center">
                <BrandHeader />
              </div>
              <NavLinks onNavigate={() => setMobileOpen(false)} />
              <UserFooter />
            </SheetContent>
          </Sheet>
          <div className="font-display text-sm">Marketing Parrucchieri</div>
          <div className="h-10 w-10 rounded-full bg-[var(--mp-yellow)] grid place-items-center font-display text-xs">
            {initials || "MP"}
          </div>
        </header>

        <div className="p-5 pb-24 sm:p-6 sm:pb-28 lg:p-10 lg:pb-28 max-w-6xl mx-auto">
          <DpaBanner />
          <Outlet />
          <div className="mt-12 pt-6 border-t border-[var(--mp-border)] flex items-center justify-between flex-wrap gap-3">
            <div className="text-[11px] text-[var(--mp-muted)]">
              {user?.email} · {roleBadge(user?.role, user?.status)}
            </div>
            <Button
              variant="outline"
              data-testid="page-footer-logout"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="rounded-full border-[var(--mp-ink)] h-9 text-sm"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Esci
            </Button>
          </div>
        </div>
      </main>

      <ClientChatBubble user={user} />
    </div>
  );
}
